/**
 * Utility class for matching staff-related field patterns in audit trail events
 */
export class StaffFieldMatcher {
  /**
   * Regex patterns for staff-related fields
   */
  private static readonly STAFF_FIELD_PATTERNS = [
    /.*_updated_by$/i, // matches any field ending with _updated_by
    /.*_updatedby$/i, // matches any field ending with _updatedby
    /.*_created_by$/i, // matches any field ending with _created_by
    /.*_createdby$/i, // matches any field ending with _createdby
    /^clt_rep_id$/i, // matches exact clt_rep_id
    /.*_staff_id$/i, // matches any field ending with _staff_id
    /.*_assigned_to$/i, // matches any field ending with _assigned_to
    /.*_assignedto$/i, // matches any field ending with _assignedto
    /.*_user_id$/i, // matches any field ending with _user_id
    /.*_userid$/i, // matches any field ending with _userid
    /.*_modified_by$/i, // matches any field ending with _modified_by
    /.*_modifiedby$/i, // matches any field ending with _modifiedby
    /.*_rep_id$/i, // matches any field ending with _rep_id
    /.*_representative_id$/i, // matches any field ending with _representative_id
    /.*_owner_id$/i, // matches any field ending with _owner_id
    /.*_manager_id$/i, // matches any field ending with _manager_id
  ];

  /**
   * Check if a field name matches staff-related patterns
   * @param fieldName - The field name to check
   * @returns true if the field matches any staff pattern, false otherwise
   */
  static isStaffField(fieldName: string): boolean {
    if (!fieldName || typeof fieldName !== 'string') {
      return false;
    }

    return this.STAFF_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName));
  }

  /**
   * Extract staff ID from event based on field pattern matching
   * @param event - The audit trail event
   * @returns staff ID as number or null if not found/invalid
   */
  static getStaffIdFromEvent(event: any): number | null {
    if (!event?.fields) {
      return null;
    }

    // Check if the field is staff-related
    if (this.isStaffField(event.fields)) {
      // Try to get staff ID from new_value first, then old_value
      const staffId = event.new_value || event.old_value;

      // Convert to number if it's a valid numeric string
      if (staffId && !isNaN(Number(staffId))) {
        return Number(staffId);
      }
    }

    return null;
  }

  /**
   * Get all supported staff field patterns for documentation/testing
   * @returns Array of regex patterns as strings
   */
  static getSupportedPatterns(): string[] {
    return this.STAFF_FIELD_PATTERNS.map((pattern) => pattern.source);
  }

  /**
   * Test multiple field names against staff patterns
   * @param fieldNames - Array of field names to test
   * @returns Object with field names as keys and boolean results as values
   */
  static testMultipleFields(fieldNames: string[]): Record<string, boolean> {
    const results: Record<string, boolean> = {};
    fieldNames.forEach((fieldName) => {
      results[fieldName] = this.isStaffField(fieldName);
    });
    return results;
  }
}
